/**
 * Color Utility Functions
 *
 * Provides color contrast and accessibility utilities for the theming system.
 */

// ============================================
// TYPES
// ============================================
interface RGBA {
  r: number;
  g: number;
  b: number;
  a: number;
}

// ============================================
// COLOR PARSING (internal)
// ============================================

/**
 * Parse a color string to RGB values
 * Supports: #hex, #hexalpha, rgb(), rgba()
 */
function parseColor(color: string): RGBA {
  // Handle hex colors
  if (color.startsWith("#")) {
    return hexToRgba(color);
  }

  // Handle rgb/rgba
  if (color.startsWith("rgb")) {
    return parseRgb(color);
  }

  // Default fallback
  return { r: 0, g: 0, b: 0, a: 1 };
}

function hexToRgba(hex: string): RGBA {
  // Remove # if present
  hex = hex.replace("#", "");

  let r: number, g: number, b: number, a = 1;

  if (hex.length === 3) {
    // Short hex: #RGB
    r = parseInt(hex[0] + hex[0], 16);
    g = parseInt(hex[1] + hex[1], 16);
    b = parseInt(hex[2] + hex[2], 16);
  } else if (hex.length === 6) {
    // Full hex: #RRGGBB
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
  } else if (hex.length === 8) {
    // Hex with alpha: #RRGGBBAA
    r = parseInt(hex.substring(0, 2), 16);
    g = parseInt(hex.substring(2, 4), 16);
    b = parseInt(hex.substring(4, 6), 16);
    a = parseInt(hex.substring(6, 8), 16) / 255;
  } else {
    return { r: 0, g: 0, b: 0, a: 1 };
  }

  return { r, g, b, a };
}

function parseRgb(color: string): RGBA {
  const match = color.match(/rgba?\(([^)]+)\)/);
  if (!match) return { r: 0, g: 0, b: 0, a: 1 };

  const parts = match[1].split(",").map((p) => p.trim());

  return {
    r: parseInt(parts[0], 10),
    g: parseInt(parts[1], 10),
    b: parseInt(parts[2], 10),
    a: parts[3] !== undefined ? parseFloat(parts[3]) : 1,
  };
}

// ============================================
// ACCESSIBILITY UTILITIES
// ============================================

/**
 * Calculate relative luminance of a color (WCAG formula)
 */
function getLuminance(color: string): number {
  const rgba = parseColor(color);
  
  const rsRGB = rgba.r / 255;
  const gsRGB = rgba.g / 255;
  const bsRGB = rgba.b / 255;

  const r =
    rsRGB <= 0.03928
      ? rsRGB / 12.92
      : Math.pow((rsRGB + 0.055) / 1.055, 2.4);
  const g =
    gsRGB <= 0.03928
      ? gsRGB / 12.92
      : Math.pow((gsRGB + 0.055) / 1.055, 2.4);
  const b =
    bsRGB <= 0.03928
      ? bsRGB / 12.92
      : Math.pow((bsRGB + 0.055) / 1.055, 2.4);

  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/**
 * Calculate contrast ratio between two colors (WCAG formula)
 */
export function getContrastRatio(color1: string, color2: string): number {
  const l1 = getLuminance(color1);
  const l2 = getLuminance(color2);
  
  const lighter = Math.max(l1, l2);
  const darker = Math.min(l1, l2);
  
  return (lighter + 0.05) / (darker + 0.05);
}

/**
 * Get either black or white text color based on background
 */
export function getContrastText(background: string): string {
  const luminance = getLuminance(background);
  return luminance > 0.179
    ? "rgba(0, 0, 0, 0.87)"
    : "rgba(255, 255, 255, 0.87)";
}
